import { useEffect, useState } from 'react';
import type { EventProfile } from '../shared/profile';
import { api } from './api';
import { usePhotoPicker } from './usePhotoPicker';
import { ActionLabel } from './DialogLayout';

export function ProfileEditor({
  profile,
  csrf,
  maxBytes,
  busy,
  onBusy,
  onSaved,
}: {
  profile: EventProfile;
  csrf: string;
  maxBytes: number;
  busy: boolean;
  onBusy: (busy: boolean) => void;
  onSaved: (profile: EventProfile) => void;
}) {
  const [draft, setDraft] = useState<{ file: File; url: string } | null>(null);
  const [error, setError] = useState('');
  const [failedPreview, setFailedPreview] = useState(false);
  const { library, message, openLibrary } = usePhotoPicker((file) => {
    if (file.size > maxBytes) {
      setDraft(null);
      setError(`Choose a photo smaller than ${Math.round(maxBytes / 1024 / 1024)} MB.`);
      return;
    }
    setDraft({ file, url: URL.createObjectURL(file) });
    setFailedPreview(false);
    setError('');
  });
  useEffect(
    () => () => {
      if (draft) URL.revokeObjectURL(draft.url);
    },
    [draft],
  );
  async function save(reset = false) {
    if (busy || (!reset && !draft)) return;
    onBusy(true);
    setError('');
    try {
      const next = reset ? await api.resetProfile(csrf) : await api.saveProfile(draft!.file, csrf);
      onSaved(next);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Couldn’t save the profile photo. Please retry.');
    } finally {
      onBusy(false);
    }
  }
  return (
    <>
      <p className="eyebrow">Family controls</p>
      <h2>Lloyd’s profile photo.</h2>
      <p>
        Choose an authentic photo of Lloyd. The circular preview shows the centred crop everyone will see.
      </p>
      <div className={'profile-edit-preview ' + (!draft && !profile.custom ? 'original-portrait' : '')}>
        {failedPreview ? (
          <span>Preview unavailable. Choose a JPEG or PNG.</span>
        ) : (
          <img
            src={draft?.url ?? profile.imageUrl}
            alt="Profile photo preview"
            onError={() => setFailedPreview(true)}
          />
        )}
      </div>
      <input ref={library} id="profile-file" type="file" accept="image/*" hidden />
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {message && !draft && <p role="status">{message}</p>}
      <div className="modal-actions">
        <button className="secondary-button" disabled={busy} onClick={openLibrary}>
          {draft ? 'Choose another photo' : 'Choose photo'}
        </button>
        <button className="button" disabled={busy || !draft || failedPreview} onClick={() => void save()}>
          <ActionLabel busy={busy} idle="Save profile photo" working="Saving…" />
        </button>
      </div>
      {busy && <p role="status">Saving the profile photo. Please keep this page open.</p>}
      {profile.custom && (
        <button className="text-button" disabled={busy} onClick={() => void save(true)}>
          Restore original portrait
        </button>
      )}
      <p className="fine-print">
        This changes only the profile picture, not the cover or the album. JPEG, PNG and WebP are supported.
      </p>
    </>
  );
}
